// Phantom slip history panel. Reused by /brief/history and the legacy
// /dashboard. Read-only audit-style list of resolved Phantom cycles
// for the authenticated user.
//
// 9C.10c: row + header cards spread cardLow + transitionStandard via
// sibling rails; header + each row eyebrow gain registered icons;
// status pills migrated to shared pillSuccessOutline /
// pillDangerOutline / pillNeutral; currency unified via
// formatVaultCedis. Behavior preserved verbatim — usePhantomCycleHistory
// hook, every render branch, all status logic, all fallback semantics.

import type { CSSProperties } from "react";

import { Icon } from "../../components/icons/Icon.tsx";
import type { IconKey } from "../../components/icons/icon-types.ts";
import { formatVaultCedis } from "../../modes/vault/vault-currency.ts";
import {
  usePhantomCycleHistory,
  type PhantomCycleHistoryState,
} from "../../phantom/usePhantomCycleHistory.ts";
import { type PhantomCycleRow, type PhantomSelection } from "../../phantom/usePhantomCycle.ts";
import { pillDangerOutline, pillNeutral, pillSuccessOutline } from "../../styles/status-pills.ts";
import * as d from "./dashboard.styles.ts";
import * as h from "./phantom-slip-history-panel.styles.ts";
import * as r from "./phantom-slip-history-panel-rails.styles.ts";

interface ResultPayload {
  resolved_at?: string;
  bust_leg_index?: number | null;
  bust_fixture_id?: string | null;
}

const cardStyle: CSSProperties = { ...d.card, ...r.cardRails };

function HeaderEyebrow({ icon, label }: { icon: IconKey; label: string }): JSX.Element {
  return (
    <span style={r.headerLead}>
      <Icon name={icon} size="sm" decorative />
      <span style={d.cardEyebrow}>{label}</span>
    </span>
  );
}

function formatResolvedAt(raw: string | undefined | null): string {
  if (typeof raw !== "string" || raw.length === 0) return "—";
  const d0 = new Date(raw);
  if (Number.isNaN(d0.getTime())) return raw;
  return d0.toUTCString();
}

function deriveBustLabel(selections: PhantomSelection[], result: ResultPayload): string {
  const idx = result.bust_leg_index ?? null;
  const sel = idx !== null && idx >= 0 ? selections[idx] : undefined;
  if (
    sel?.home_team !== undefined &&
    sel?.home_team !== null &&
    sel?.away_team !== undefined &&
    sel?.away_team !== null
  ) {
    return `${sel.home_team} vs ${sel.away_team}`;
  }
  return result.bust_fixture_id ?? "(fixture)";
}

function statusLabel(status: string): string {
  if (status === "phantom_won") return "Won (simulated)";
  if (status === "phantom_busted") return "Busted (simulated)";
  if (status === "expired") return "Expired (simulated)";
  return status;
}

function rowStyleFor(status: string): CSSProperties {
  if (status === "phantom_won") return { ...h.rowWon, ...r.cardRails };
  if (status === "phantom_busted") return { ...h.rowBusted, ...r.cardRails };
  return { ...h.rowExpired, ...r.cardRails };
}

function pillStyleFor(status: string): CSSProperties {
  if (status === "phantom_won") return pillSuccessOutline;
  if (status === "phantom_busted") return pillDangerOutline;
  return pillNeutral;
}

function rowIconFor(status: string): IconKey {
  if (status === "phantom_won") return "lifecycle.complete";
  if (status === "phantom_busted") return "process.process_warning";
  return "lifecycle.rejected";
}

function HistoryRow({ cycle }: { cycle: PhantomCycleRow }): JSX.Element {
  const selections = (
    Array.isArray(cycle.selections) ? cycle.selections : []
  ) as PhantomSelection[];
  const result = (cycle.result ?? {}) as ResultPayload;
  const isBusted = cycle.status === "phantom_busted";

  return (
    <div style={rowStyleFor(cycle.status)}>
      <div style={h.rowHeader}>
        <span style={r.rowLead}>
          <Icon name={rowIconFor(cycle.status)} size="sm" decorative />
          <span style={pillStyleFor(cycle.status)}>{statusLabel(cycle.status)}</span>
        </span>
        <span style={h.resolvedAt}>resolved · {formatResolvedAt(result.resolved_at)}</span>
      </div>
      <div style={h.metaGrid}>
        <span style={h.metaItem}>
          <span style={h.metaKey}>stake</span>
          <span style={h.metaValue}>{formatVaultCedis(cycle.stake_amount)} · simulated</span>
        </span>
        <span style={h.metaItem}>
          <span style={h.metaKey}>fixtures</span>
          <span style={h.metaValue}>{String(selections.length)}</span>
        </span>
        <span style={h.metaItem}>
          <span style={h.metaKey}>type</span>
          <span style={h.metaValue}>{cycle.result_type ?? "—"}</span>
        </span>
        <span style={h.metaItem}>
          <span style={h.metaKey}>source</span>
          <span style={h.metaValue}>{cycle.result_source ?? "—"}</span>
        </span>
      </div>
      {isBusted ? (
        <span style={h.bustLine}>
          first losing leg: {deriveBustLabel(selections, result)} (simulated)
        </span>
      ) : null}
      <span style={h.safetyLine}>No real match result was used. No real money was involved.</span>
    </div>
  );
}

function EmptyHistory(): JSX.Element {
  return (
    <section style={cardStyle} aria-label="Phantom slip history">
      <HeaderEyebrow icon="lifecycle.settled" label="Phantom history" />
      <h2 style={d.cardTitle}>No resolved Phantom slips yet</h2>
      <p style={d.cardBodySm}>
        Resolved Phantom slips will appear here after you create, confirm, and resolve a simulated
        slip in the panel above. This list is Phantom-only — no real money is ever involved and no
        live match result is consulted.
      </p>
    </section>
  );
}

function LoadingHistory(): JSX.Element {
  return (
    <section style={cardStyle} aria-label="Phantom slip history">
      <HeaderEyebrow icon="lifecycle.settled" label="Phantom history" />
      <h2 style={d.cardTitle}>Loading Phantom slip history…</h2>
      <p style={d.cardBodySm}>Reading slip_cycles for this user.</p>
    </section>
  );
}

export function PhantomSlipHistoryPanel(): JSX.Element {
  const { cycles, loading, error }: PhantomCycleHistoryState = usePhantomCycleHistory();

  if (loading) return <LoadingHistory />;
  if (cycles.length === 0 && error === null) return <EmptyHistory />;

  return (
    <section style={cardStyle} aria-label="Phantom slip history">
      <HeaderEyebrow icon="lifecycle.settled" label="Phantom history" />
      <h2 style={d.cardTitle}>Resolved Phantom slips</h2>
      <p style={d.cardBodySm}>
        Most recent first. Every row below is a simulated outcome from the deterministic Phantom
        resolver — no live match result, no real money, no agent reasoning yet.
      </p>
      {error ? <div style={h.errorBar}>{error}</div> : null}
      <ul style={h.list}>
        {cycles.map((cycle) => (
          <li key={cycle.id}>
            <HistoryRow cycle={cycle} />
          </li>
        ))}
      </ul>
    </section>
  );
}
